// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import {ApolloError} from 'apollo-client';
import React, {useEffect, useState} from 'react';
import {RouteComponentProps, withRouter} from 'react-router';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Form, Modal} from 'semantic-ui-react';

import {
  useAllProjectNamesLazyQuery,
  useRenameProjectMutation,
} from '../../generated/graphql';
import {PROTECTED_PROJECT_NAMES} from '../../pages/HomePage/HomePageSidebar/CreateProjectDrawer';
import {
  extractErrorMessageFromApolloError,
  propagateErrorsContext,
} from '../../util/errors';
import {sanitizeName} from '../../util/sanitize';

type Props = {
  open: boolean;
  targetProject: {name: string; entityName: string};
  onRename?(): void;
  onClose(): void;
} & RouteComponentProps;

const ProjectRenameModal: React.FC<Props> = props => {
  const [tempName, setTempName] = useState(props.targetProject.name);
  const [error, setError] = useState<string | null>(null);
  const [renameProject] = useRenameProjectMutation();

  const [fetchAllProjectNames, allProjectNames] = useAllProjectNamesLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  useEffect(() => {
    if (props.open) {
      fetchAllProjectNames({
        variables: {entityName: props.targetProject.entityName, first: 1000},
      });
    }
  }, [props.open, fetchAllProjectNames, props.targetProject.entityName]);

  useEffect(() => {
    setTempName(props.targetProject.name);
  }, [props.open, props.targetProject.name]);

  const nameInUse = (name: string) => {
    if (!allProjectNames.data) {
      return false;
    }

    if (name === props.targetProject.name) {
      // we don't want it to look like an error before the user enters anything
      return false;
    }

    return !!allProjectNames.data.projects?.edges.find(
      edge => edge.node && edge.node.name === name
    );
  };

  const canRename =
    tempName !== '' &&
    !allProjectNames.loading &&
    !nameInUse(tempName) &&
    tempName !== props.targetProject.name;

  const localNameInUseError = nameInUse(tempName) && {
    content: `Project with name "${tempName}" already exists`,
  };

  const localNameProtectedError = PROTECTED_PROJECT_NAMES.has(tempName) && {
    content: `Project with name "${tempName}" is protected`,
  };

  const handleSubmit = () => {
    if (!canRename) {
      return;
    }

    renameProject({
      variables: {
        entityName: props.targetProject.entityName,
        oldProjectName: props.targetProject.name,
        newProjectName: tempName,
      },
      context: propagateErrorsContext(),
    })
      .then(() => {
        props.onRename != null
          ? props.onRename()
          : props.history.replace(
              `/${props.targetProject.entityName}/${encodeURIComponent(
                tempName
              )}/overview`
            );
        props.onClose();
      })
      .catch((e: ApolloError) => {
        const msg = extractErrorMessageFromApolloError(e);
        if (msg) {
          setError(msg);
        }
      });
  };

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      onClick={(e: React.SyntheticEvent) => {
        e.stopPropagation();
      }}
      className="project-rename-modal">
      <Modal.Header>
        <span className="warning-header">
          <LegacyWBIcon name="warning" size="large" />
          <span className="warning-text">Warning</span>
        </span>
      </Modal.Header>
      <Modal.Content>
        <p>
          Renaming breaks all existing links to your projects, runs, and
          reports.
        </p>
        <p>
          You will need to update any links you've shared as well as calls to{' '}
          <code>wandb.init(project="{props.targetProject.name}")</code> in your
          code.
        </p>

        <p>Are you sure you want to continue?</p>

        <Form onSubmit={handleSubmit}>
          <Form.Input
            type="text"
            value={tempName}
            error={
              localNameInUseError ||
              localNameProtectedError ||
              (error && {content: error})
            }
            onChange={(_, {value}) => setTempName(sanitizeName(value))}
            placeholder="Enter a new project name"
            autoFocus
          />
        </Form>
      </Modal.Content>

      <Modal.Actions>
        <Form onSubmit={handleSubmit}>
          <Button
            variant="secondary"
            size="medium"
            className="mr-8"
            onClick={props.onClose}>
            Cancel
          </Button>
          <Button
            variant={canRename ? 'destructive' : 'ghost'}
            size="medium"
            type="submit"
            disabled={!canRename}>
            Rename Project
          </Button>
        </Form>
      </Modal.Actions>
    </Modal>
  );
};

export default withRouter(ProjectRenameModal);
